import { useEffect, useState } from 'react';
import {
  Box, Button, FormControl, FormLabel, HStack, Select, SimpleGrid, Text
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import {
  buildShowcaseCandidatesPath,
  type ShowcaseCandidateResponse
} from './showcaseCandidateQuery';

type CampaignRow = {
  id: string;
  name: string;
  platform: string;
  kind: string | null;
  status: string | null;
};

const LABEL = {
  fontSize: '11px',
  fontWeight: '700',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  color: 'brand.muted'
};

const QC_LABELS: Record<string, string> = {
  product_fidelity: 'Product fidelity',
  competitor_marks: 'Competitor marks',
  layout_safe_box: 'Layout safe box',
  text_defects: 'Text defects',
  benefit_accuracy: 'Benefit accuracy'
};

const BASE_CATEGORIES = [
  'product_fidelity',
  'competitor_marks',
  'layout_safe_box',
  'text_defects'
];

function categoryLabel(name: string) {
  return QC_LABELS[name] || name;
}

type Props = {
  brandId: string;
  onFill: (ids: string[]) => void;
};

export function PickFromCampaign({ brandId, onFill }: Props) {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [campaignId, setCampaignId] = useState('');
  const [kind, setKind] = useState('');
  const [format, setFormat] = useState('');
  const [qc, setQc] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ShowcaseCandidateResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCampaigns([]);
    setCampaignId('');
    setKind('');
    setFormat('');
    setQc('');
    setResult(null);
    apiJson<{ campaigns: CampaignRow[] }>(`/api/campaigns?brandId=${encodeURIComponent(brandId)}`)
      .then(res => { if (!cancelled) setCampaigns(res.campaigns || []); })
      .catch(() => { if (!cancelled) setCampaigns([]); });
    return () => { cancelled = true; };
  }, [brandId]);

  useEffect(() => {
    if (!campaignId) {
      setResult(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const path = buildShowcaseCandidatesPath({
      brandId,
      campaignId,
      kind,
      platformFormat: format,
      qc
    });
    apiJson<ShowcaseCandidateResponse>(path)
      .then(res => { if (!cancelled) setResult(res); })
      .catch(e => {
        if (!cancelled) {
          setResult(null);
          setError(e instanceof Error ? e.message : 'Could not load ads');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId, campaignId, kind, format, qc]);

  const counts = result?.counts;
  const extraCategories = Object.keys(counts?.byCategory || {})
    .filter(name => !BASE_CATEGORIES.includes(name));
  const categoryOptions = [...BASE_CATEGORIES, ...extraCategories];

  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" p={4}>
      <Text fontSize="sm" fontWeight="700" color="brand.ink" mb={3}>
        Pick from campaign
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel {...LABEL}>Campaign</FormLabel>
          <Select
            value={campaignId}
            placeholder="Choose a campaign"
            onChange={e => {
              setCampaignId(e.target.value);
              setFormat('');
              setQc('');
            }}
          >
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel {...LABEL}>Kind</FormLabel>
          <Select value={kind} onChange={e => setKind(e.target.value)} isDisabled={!campaignId}>
            <option value="">All kinds</option>
            <option value="image">Static</option>
            <option value="video">Video</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel {...LABEL}>Platform format</FormLabel>
          <Select value={format} onChange={e => setFormat(e.target.value)} isDisabled={!campaignId}>
            <option value="">All formats</option>
            {(result?.facets.formats || []).map(f => (
              <option key={f.value} value={f.value}>{f.value} ({f.count})</option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel {...LABEL}>Vision QC</FormLabel>
          <Select value={qc} onChange={e => setQc(e.target.value)} isDisabled={!campaignId}>
            <option value="">Any result</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed any category</option>
            <option value="uninspected">Not inspected</option>
            {categoryOptions.map(name => (
              <option key={name} value={`failed:${name}`}>Failed: {categoryLabel(name)}</option>
            ))}
          </Select>
        </FormControl>
      </SimpleGrid>

      {campaignId && counts && (
        <Box mt={3}>
          <Text fontSize="sm" color="brand.ink">
            {counts.eligible} eligible · {counts.passed} passed · {counts.failed} failed · {counts.uninspected} not inspected
          </Text>
          <Text fontSize="sm" color="brand.muted" mt={1}>
            {categoryOptions.map(name => `${categoryLabel(name)} ${counts.byCategory[name] || 0}`).join(' · ')}
          </Text>
          <Text fontSize="xs" color="brand.muted" mt={1}>
            Category counts overlap. Passed means every scored category on the latest check cleared its floor.
          </Text>
          {counts.overflow && (
            <Text fontSize="sm" color="orange.700" mt={2}>
              {counts.returned} of {counts.matched} matching ads fit in one showcase (cap {counts.cap}). Fill writes those {counts.returned}.
            </Text>
          )}
        </Box>
      )}
      {error && <Text fontSize="sm" color="red.600" mt={2}>{error}</Text>}

      <HStack mt={4} justify="space-between" flexWrap="wrap" spacing={3}>
        <Text fontSize="xs" color="brand.muted">Fill replaces the ID box. Paste and upload still work.</Text>
        <Button
          size="sm"
          variant="outline"
          isDisabled={!result || result.ads.length === 0 || loading}
          isLoading={loading}
          onClick={() => result && onFill(result.ads.map(ad => ad.id))}
        >
          Fill ID box
        </Button>
      </HStack>
    </Box>
  );
}
